import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { ReactiveFormsModule, FormControl, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { NgToastService } from 'ng-angular-popup';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { OrganizationService } from '@/app/services/organization/organization.service';
import { IttWrapperComponent } from '@/app/components/projects/project-development/tendering/inivitation-to-tender/itt-sender-side/itt-creation/itt-wrapper/itt-wrapper.component';
import { MatIcon } from '@angular/material/icon';
import { TenderingService } from '@/app/services/tendering-services/tendering.service';
import { ActivatedRoute, Router } from '@angular/router';
import { RfpLabelService } from '../../../inivitation-to-tender/rfp-labels.service';

@Component({
  selector: 'app-companies-selection',
  standalone: true,
  imports: [
    MatChipsModule,
    MatButtonModule,
    MatSelectModule,
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIcon,
  ],
  templateUrl: './companies-selection.component.html',
  styleUrl: './companies-selection.component.css',
})
export class CompaniesSelectionComponent implements OnInit {
  @Output() companiesSelectionDataEmitter: EventEmitter<any> =
    new EventEmitter<any>();
  @Input() ITTDeveloperData: any;

  projectId: number;
  projectName: string;

  filteredOrganizations: any[] = [];
  isExtCompany = false;

  tenderTypeControl = new FormControl('', Validators.required);
  selectedOrganizationControl = new FormControl();
  extCompanyNameControl = new FormControl();
  nameControl = new FormControl('', Validators.required);
  positionControl = new FormControl('', Validators.required);
  emailControl = new FormControl('', [Validators.required, Validators.email]);
  searchTermControl = new FormControl('');

  organizations: any;
  receiverOrganizationsList: any[] = []; // Array to store the organization details
  isRFPorRFQ: string;
  labels: any;

  constructor(
    private toast: NgToastService,
    private OrganizationService: OrganizationService,
    private ittSectionsComponent: IttWrapperComponent,
    private tenderingService: TenderingService,
    private route: ActivatedRoute,
    private router: Router,
    private rfpLabelService: RfpLabelService
  ) {}

  ngOnInit(): void {
    // Subscribe to route changes
    this.route.url.subscribe(() => {
      this.updateRFPorRFQFlag();
      const currentRoute = this.router.url; // Get full URL path
      this.rfpLabelService.updateLabelsFromRoute(currentRoute);
    });
    this.rfpLabelService.labels$.subscribe((labels) => {
      this.labels = labels;
    });
    this.fetchOrganization();
    this.route.queryParams.subscribe((params) => {
      this.projectName = params['projectName'];
    });
    this.route.paramMap.subscribe((params) => {
      this.projectId = params.get('projectId')
        ? parseInt(params.get('projectId'))
        : null;
    });

    /**
     * NOTE: On edit or when we go back and forth between wizards, we will
     *  set initial value if it exists
     */
    if (this.ITTDeveloperData?.receiverOrganizations?.length > 0) {
      this.ITTDeveloperData?.receiverOrganizations.forEach((org) => {
        const receiverOrganizationDetail = {
          company_name:
            org?.applicant_organization?.name ?? org?.external_company_name,
          name: org?.name,
          email: org?.email,
          position: org?.position,
          id: org?.receiving_org_id || null,
          type: org?.receiving_org_id ? 'system' : 'external',
        };
        this.receiverOrganizationsList.push(receiverOrganizationDetail);
      });
    }
    this.tenderTypeControl.setValue(this.ITTDeveloperData?.tendering_type);
  }

  updateRFPorRFQFlag() {
    const currentRoute = this.router.url.toLowerCase(); // Get full URL path
    if(currentRoute.includes('rfp')){
      this.isRFPorRFQ = 'RFP';
    }else if(currentRoute.includes('rfq')){
      this.isRFPorRFQ = 'RFQ'
    }
    console.log('isRFPorRFQ:', this.isRFPorRFQ);
  }

  // On initial load, get all organizations
  fetchOrganization() {
    this.OrganizationService.getAllOrganization().subscribe({
      next: (response: any) => {
        if (response?.meta?.success) {
          this.organizations = response.data;
          this.filteredOrganizations = [...this.organizations];
          return;
        }
        this.toast.danger('Failed to fetch Organizations');
      },
      error: (error: any) => {
        this.toast.danger('Failed to fetch Organizations', error);
      },
    });
  }

  // when user selects other from company dropdown, show external company input
  externalCompanySelection() {
    this.isExtCompany = true;
    this.extCompanyNameControl.setValue('');
    this.nameControl.setValue('');
    this.emailControl.setValue('');
    this.positionControl.setValue('');
    this.extCompanyNameControl.setValidators([Validators.required]);
  }

  /**
   * When user clicks on Add button to add system/external organization
   * to the table for sending to backend
   */
  addReceiverOrganizationDetail() {
    /**
     * If user has entered value in any of the three fields and have not selected a company yet
     */
    if (
      (this.nameControl.value ||
        this.emailControl.value ||
        this.positionControl.value) &&
      !this.selectedOrganizationControl.value
    ) {
      this.selectedOrganizationControl.setErrors({ required: true });
      this.selectedOrganizationControl.markAsTouched();
    }

    this.nameControl.markAsTouched();
    this.emailControl.markAsTouched();
    this.positionControl.markAsTouched();
    this.selectedOrganizationControl.markAsTouched();
    if (this.isExtCompany) {
      this.extCompanyNameControl.markAsTouched();
    }

    // Validation check
    if (
      (!this.selectedOrganizationControl.value &&
        !this.extCompanyNameControl.value) || // At least one should be present
      !this.nameControl.value ||
      !this.emailControl.value ||
      !this.positionControl.value
    ) {
      this.toast.warning('Please fill the required fields', 'ERROR');
      return;
    }

    // If validation passes, create the object
    const receiverOrganizationDetail = {
      company_name: this.selectedOrganizationControl?.value?.name
        ? this.selectedOrganizationControl?.value?.name
        : this.extCompanyNameControl.value || null,
      name: this.nameControl.value,
      email: this.emailControl.value,
      position: this.positionControl.value,
      id: this.selectedOrganizationControl?.value?.id || null,
      type: this.selectedOrganizationControl?.value?.id ? 'system' : 'external',
    };

    // insert currently added organization to the receiverOrganizationsList array
    this.receiverOrganizationsList.push(receiverOrganizationDetail); // Add the object to the array

    this.toast.success('Organization added', 'success');
    // Clear fields and reset the form controls
    this.resetFormControls(true);
    // Remove current organization from the dropdown list
    this.filterReceiverOrganizationList();
  }

  /**
   * NOTE: removed all organizations that are in receiverOrganizationsList from filteredOrganizations
   */
  filterReceiverOrganizationList() {
    this.filteredOrganizations = this.organizations.filter(
      (organization) =>
        !this.receiverOrganizationsList.some(
          (receiverOrganization) => receiverOrganization.id == organization.id
        )
    );
  }

  prepareData(org: any) {
    const data = {
      id: org?.id ? org?.id : '',
      name: this.extCompanyNameControl.value
        ? this.extCompanyNameControl.value
        : '',
    };
    return data;
  }

  /**
   * when user click an internal organization or add an external
   * organization get its record from the database
   */
  getOrganizationDetails(org: any) {
    if (org?.id) {
      this.isExtCompany = false;
      this.extCompanyNameControl.setValue('');
    }

    const dataToSubmit = this.prepareData(org);

    this.OrganizationService.getTenderingOrganizationsDetails(
      dataToSubmit,
      this.projectId,
      'itt'
    ).subscribe({
      next: (response: any) => {
        if (response?.meta?.success && response?.data) {
          this.nameControl.setValue(response.data.name || '');
          this.emailControl.setValue(response.data.email || '');
          this.positionControl.setValue(response.data.position || '');
        }
      },
      error: (error: any) => {
        console.error('API Error:', error);
        this.resetFormControls();
      },
    });
  }

  // when user deletes an organization from the selected receiver organization list
  removeReceiverOrganization(org: any) {
    const index = this.receiverOrganizationsList.indexOf(org);
    if (index >= 0) {
      this.receiverOrganizationsList.splice(index, 1);
      if (org?.id) {
        this.filterReceiverOrganizationList();
      }
    }
  }

  // On search to show only
  filterOrganizationsBySearchTerm() {
    const search = this.searchTermControl?.value?.toLowerCase();
    this.filteredOrganizations = this.organizations.filter(
      (org: any) => org.name.toLowerCase().includes(search) // Adjust field access based on your API response
    );
  }

  resetFormControls(resetAll: boolean = false) {
    if (resetAll) {
      this.selectedOrganizationControl.reset();
      this.extCompanyNameControl.reset();
      this.nameControl.reset();
      this.emailControl.reset();
      this.positionControl.reset();
      this.isExtCompany = false; // Reset 'other' selection
    } else {
      this.nameControl.setValue('');
      this.emailControl.setValue('');
      this.positionControl.setValue('');
    }
  }

  moveToPreviousStep() {
    this.ittSectionsComponent.previousStep();
  }

  prepareDataToSubmit() {
    this.tenderTypeControl.markAsTouched();

    if (this.tenderTypeControl.value && this.receiverOrganizationsList.length) {
      const data = {
        tendering_type: this.tenderTypeControl.value,
        receiving_org_data: this.receiverOrganizationsList,
      };
      return data;
    } else {
      if (!this.receiverOrganizationsList.length) {
        this.selectedOrganizationControl.setErrors({ required: true });
        this.selectedOrganizationControl.markAsTouched();
        this.nameControl.markAsTouched();
        this.emailControl.markAsTouched();
        this.positionControl.markAsTouched();
        this.toast.warning('Please fill the required fields', 'WARNING', 4000);
      }
    }
  }

  moveToNextStep() {
    const dataToSubmit = this.prepareDataToSubmit();

    if (dataToSubmit) {
      this.tenderingService
        .storeDeveloperITTOrganizationDetailsData(dataToSubmit, this.projectId)
        .subscribe({
          next: (response: any) => {
            if (response?.meta?.success) {
              this.toast.success('Companies added successfully', 'SUCCESS');
              this.companiesSelectionDataEmitter.emit(response?.data);
              this.ittSectionsComponent.nextStep();
              return;
            }
            this.toast.danger('Companies could not be added', 'DANGER', 4000);
          },
          error: (error: any) => {
            console.error('Failed to submit:', error);
            this.toast.danger(error.error.message, 'DANGER', 4000);
          },
        });
    }
  }
}
